import React from 'react';
import PropTypes from 'prop-types';
import { dateService } from '../../domain/services/dateService';
import { SmartLink } from '../SmartLink';
import { SponsoredTag } from '../SponsoredTag';
import { t } from '../../domain/services/configService';
import { IconGalleryTrace } from '../Icon/IconGalleryTrace';
import { IconWatch } from '../Icon/IconWatch';
import { IconExplore } from '../Icon/IconExplore';
import { IconRead } from '../Icon/IconRead';

const ctaConfig = {
  video: { component: IconWatch, text: 'watch' },
  photo_gallery: { component: IconGalleryTrace, text: 'gallery' },
  page: { component: IconExplore, text: 'explore' },
  default: { component: IconRead, text: 'read' }
};

export const CardText = ({
  type,
  url,
  nativeLink,
  onClick,
  target,
  title,
  subtitle,
  videoDuration,
  sponsored,
  taxonomies,
  showCTA,
  styles,
  dataLayerClickEvent
}) => {
  const ptt = taxonomies && taxonomies.find(term => term.isPtt);

  const headerItems = [];
  if (sponsored) {
    headerItems.push(
      <div css={styles.sponsored} key="sponsored">
        {/* Do not pass bgColor to SponsoredTag, to be able to style it from src/components/Paragraph/ContentPackage/style/*.js */}
        <SponsoredTag text={sponsored.text} url={sponsored.url} />
      </div>
    );
  } else {
    if (ptt && ptt.name) {
      headerItems.push(
        <span key={ptt.name}>
          {ptt.url ? <SmartLink to={ptt.url}>{ptt.name}</SmartLink> : ptt.name}
        </span>
      );
    }
    if (videoDuration) {
      headerItems.push(
        <span key="videoDuration">
          {dateService.secondsToMinutes(videoDuration)}
        </span>
      );
    }
  }

  const CtaIconComponent = showCTA
    ? ctaConfig[type]?.component || ctaConfig.default.component
    : null;
  const ctaText = showCTA
    ? ctaConfig[type]?.text || ctaConfig.default.text
    : null;

  return (
    <div css={styles.container}>
      {headerItems.length > 0 && (
        <div css={styles.taxonomy}>
          {headerItems.map(headerItem => headerItem)}
        </div>
      )}

      {title && (
        <h2 css={styles.title}>
          <SmartLink
            to={url}
            native={nativeLink}
            onClick={onClick}
            target={target}
            title={title}
            dataLayerClickEvent={dataLayerClickEvent}
          >
            {title}
          </SmartLink>
        </h2>
      )}

      {subtitle ? <p css={styles.subtitle}>{subtitle}</p> : null}

      {showCTA && (
        <div css={styles.cta}>
          <SmartLink
            to={url}
            native={nativeLink}
            onClick={onClick}
            target={target}
            title={title}
            dataLayerClickEvent={dataLayerClickEvent}
          >
            <CtaIconComponent size={20} />
            {t(`__CTA_${ctaText.toUpperCase()}__`)}
          </SmartLink>
        </div>
      )}
    </div>
  );
};

CardText.defaultProps = {
  nativeLink: false,
  subtitle: undefined,
  videoDuration: undefined,
  sponsored: undefined,
  taxonomies: undefined,
  showCTA: false,
  dataLayerClickEvent: undefined,
  onClick: undefined,
  target: undefined,
  title: undefined
};

CardText.propTypes = {
  type: PropTypes.oneOf([
    'article',
    'video',
    'show',
    'photo_gallery',
    'hub_page',
    'page'
  ]).isRequired,
  url: PropTypes.string.isRequired,
  nativeLink: PropTypes.bool,
  onClick: PropTypes.func,
  target: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  videoDuration: PropTypes.number,
  sponsored: PropTypes.shape({
    text: PropTypes.string.isRequired,
    bgColor: PropTypes.string,
    url: PropTypes.string
  }),
  taxonomies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
      isPtt: PropTypes.bool
    })
  ),
  showCTA: PropTypes.bool,
  styles: PropTypes.shape({
    container: PropTypes.any,
    sponsored: PropTypes.any,
    taxonomy: PropTypes.any,
    title: PropTypes.any,
    subtitle: PropTypes.any,
    cta: PropTypes.any
  }).isRequired,
  dataLayerClickEvent: PropTypes.objectOf(PropTypes.any)
};
